/**
 * Edge Path Utilities
 * 
 * Public API for edge path calculations.
 * Smart orthogonal routing that:
 * 1. Never crosses through nodes
 * 2. Takes the shortest possible path
 * 3. Separates overlapping edges to make them distinct
 * 4. Dynamically chooses optimal connection points on any edge of a node
 * 
 * DYNAMIC CONNECTION POINTS: Edges can connect from/to any side of a node
 * (top, bottom, left, right) based on what produces the shortest, cleanest path.
 * 
 * @see edgePaths/connectionPoints.js - Connection point calculation
 * @see edgePaths/previewPaths.js - Preview path generation
 * @see edgePaths/edgeGeneration.js - Edge generation with offset optimization
 */

import { NODE_WIDTH, NODE_HEIGHT, CLEARANCE } from './constants';
import { routeDownward, routeUpward, routeHorizontal, routeAround } from './routing';
import { ConnectionSide, getConnectionPoint, calculateOptimalConnection } from './edgePaths/connectionPoints';
import {
  buildObstacleSpatialIndex,
  runWithObstacleSpatialIndex,
  OBSTACLE_SPATIAL_INDEX_MIN_NODES,
} from './pathHelpers';
// Re-export from submodules
export { ConnectionSide, getConnectionPoint, calculateOptimalConnection } from './edgePaths/connectionPoints';
export { getPreviewPath, getPreviewPathFromPoint } from './edgePaths/previewPaths';
export { generateEdges } from './edgePaths/edgeGeneration';
export { computeGapLaneAssignments } from './gapLaneAssignment';

// Re-export constants for external use
export { NODE_WIDTH, NODE_HEIGHT };

function inferPathTypeFromPoints(startPoint, endPoint) {
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy >= 0 ? 'downward' : 'upward';
  }
  return dx >= 0 ? 'rightward' : 'leftward';
}

// ═══════════════════════════════════════════════════════════════
// EDGE PATH CALCULATION
// ═══════════════════════════════════════════════════════════════

/**
 * Calculate path from source to target, routing around any blocking nodes
 * ENHANCED: Dynamically chooses optimal connection points on any edge of the nodes
 * 
 * @param {Object} fromPos - Source node position { x, y }
 * @param {Object} toPos - Target node position { x, y }
 * @param {Object} fromDims - Source node dimensions { width, height }
 * @param {Object} toDims - Target node dimensions { width, height }
 * @param {Array} allNodes - All nodes for obstacle detection
 * @param {number} edgeIndex - Index of this edge (for separation)
 * @param {Object} offsets - Optional edge separation offsets
 * @param {string} edgeId - Optional edge ID for hysteresis tracking
 * @param {number} fromOffset - Optional offset for the start connection point
 * @param {number} toOffset - Optional offset for the end connection point
 * @param {string} fromSideOverride - Optional override for the from side (pre-calculated)
 * @param {string} toSideOverride - Optional override for the to side (pre-calculated)
 * @param {string|null} fromNodeId - Source task id (excludes endpoint from obstacles; preferred over position match)
 * @param {string|null} toNodeId - Target task id
 * @param {object|null} [prebuiltObstacleSpatialIndex] - Optional spatial grid from `buildObstacleSpatialIndex(allNodes)` for batch pathing (same node object identity as `allNodes`).
 * @param {Object|null} [toPointOverride] - Optional fixed endpoint { x, y } (e.g. milestone convergence anchor)
 */
export function getEdgePath(fromPos, toPos, fromDims = null, toDims = null, allNodes = [], edgeIndex = 0, offsets = null, edgeId = null, fromOffset = 0, toOffset = 0, fromSideOverride = null, toSideOverride = null, laneAssignment = null, fromNodeId = null, toNodeId = null, prebuiltObstacleSpatialIndex = null, toPointOverride = null) {
  // Guard against null/undefined positions
  if (!fromPos || fromPos.x == null || fromPos.y == null ||
      !toPos || toPos.x == null || toPos.y == null) {
    console.warn('[getEdgePath] Invalid positions:', { fromPos, toPos, edgeId });
    return ''; // Return empty path to avoid SVG errors
  }
  
  // Calculate optimal connection points (or use overrides if provided)
  let fromSide, toSide, pathType;
  if (fromSideOverride && toSideOverride) {
    fromSide = fromSideOverride;
    toSide = toSideOverride;
    // Determine pathType from sides
    if (fromSide === ConnectionSide.BOTTOM && toSide === ConnectionSide.TOP) pathType = 'downward';
    else if (fromSide === ConnectionSide.TOP && toSide === ConnectionSide.BOTTOM) pathType = 'upward';
    else if (fromSide === ConnectionSide.RIGHT && toSide === ConnectionSide.LEFT) pathType = 'rightward';
    else if (fromSide === ConnectionSide.LEFT && toSide === ConnectionSide.RIGHT) pathType = 'leftward';
    else pathType = 'downward'; // Default
  } else {
    ({ fromSide, toSide, pathType } = calculateOptimalConnection(fromPos, toPos, fromDims, toDims));
  }
  
  const startPoint = getConnectionPoint(fromPos, fromDims, fromSide, fromOffset);
  const endPoint = toPointOverride || getConnectionPoint(toPos, toDims, toSide, toOffset);
  const effectivePathType = toPointOverride
    ? inferPathTypeFromPoints(startPoint, endPoint)
    : pathType;
  
  // Obstacles: exclude endpoints by stable task id when available (avoids mis-classifying
  // when positions are offset or two nodes share coordinates); else fall back to position.
  // Milestone anchor edges keep the target node as an obstacle so routes wrap the hover body.
  const obstacles = allNodes.filter(node => {
    if (!node.pos) return false;
    if (fromNodeId != null && node.id != null) {
      if (node.id === fromNodeId) return false;
      if (!toPointOverride && toNodeId != null && node.id === toNodeId) return false;
      return true;
    }
    const isSource = Math.abs(node.pos.x - fromPos.x) < 1 && Math.abs(node.pos.y - fromPos.y) < 1;
    const isTarget = !toPointOverride
      && Math.abs(node.pos.x - toPos.x) < 1
      && Math.abs(node.pos.y - toPos.y) < 1;
    return !isSource && !isTarget;
  });

  const runDownward = () =>
    routeDownward(startPoint.x, startPoint.y, endPoint.x, endPoint.y, obstacles, edgeIndex, offsets, edgeId, laneAssignment);

  const wrapDownward = () => {
    if (prebuiltObstacleSpatialIndex) {
      return runWithObstacleSpatialIndex(
        { index: prebuiltObstacleSpatialIndex, obstacleRefSet: new Set(obstacles) },
        runDownward,
      );
    }
    if (obstacles.length >= OBSTACLE_SPATIAL_INDEX_MIN_NODES) {
      return runWithObstacleSpatialIndex(
        { index: buildObstacleSpatialIndex(obstacles), obstacleRefSet: new Set(obstacles) },
        runDownward,
      );
    }
    return runDownward();
  };

  // Route based on path type
  switch (effectivePathType) {
    case 'downward':
      return wrapDownward();

    case 'upward':
      return routeUpward(startPoint.x, startPoint.y, endPoint.x, endPoint.y, fromPos, toPos, fromDims, toDims, obstacles, edgeId, laneAssignment);

    case 'rightward':
    case 'leftward':
      return routeHorizontal(startPoint.x, startPoint.y, endPoint.x, endPoint.y, fromPos, toPos, fromDims, toDims, obstacles, pathType === 'rightward', edgeId, laneAssignment);

    default:
      // Fallback to the old routing logic
      if (endPoint.y > startPoint.y + CLEARANCE) {
        return wrapDownward();
      }
      return routeAround(startPoint.x, startPoint.y, endPoint.x, endPoint.y, fromPos, toPos, fromDims, toDims, obstacles);
  }
}
